import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import Button from "src/components/Button";
import TextInput from "src/components/TextInput";
import { ActiveStrengthExerciseSet, useEndActiveWorkoutDropsetMutation, useEndActiveWorkoutSetMutation } from "src/graphql";
import { rangeUnitToString } from "../utils/range-unit-to-string";
import { CheckCircle as CheckIcon } from 'react-huge-icons/solid'
import ActiveDropsetExercise from "./active-dropset-exercise";

const ActiveStrengthExercise = (props: {
    set: ActiveStrengthExerciseSet, idx: number, workoutExerciseId: string
}) => {

    const { set, idx, workoutExerciseId } = props;

    const { t } = useTranslation();

    let w: number = set.weight?.val!;
    let r: number = set.reps?.max!;

    const { handleSubmit, register, formState: { isSubmitting } } = useForm({
        defaultValues: {
            rest: set.rest?.done || set.rest?.val,
            reps: set.reps?.done || set.reps?.val,
            weight: set.weight?.done || set.weight?.val,
        }
    });

    const dropsetsForm = useForm({
        defaultValues: {
            dropsets: set.dropsets?.map((ds) => {
                return {
                    id: ds.id,
                    reps: ds?.reps?.done || ds?.reps?.val,
                    weight: ds?.weight?.done || ds?.weight?.val
                }
            })
        }
    })

    const onSubmit = async (e: any) => {
        await endActiveWorkoutSet({
            variables: {
                input: {
                    exerciseId: workoutExerciseId,
                    setId: set.id,
                    ...e
                }
            },
            update(cache, result, options) {
                cache.modify({
                    id: cache.identify({ id: options.variables?.input.setId, __typename: 'ActiveStrengthExerciseSet' }),
                    fields: {
                        end_at() {
                            return new Date().toISOString()
                        },
                        reps(data) {
                            return {
                                ...data,
                                done: options.variables?.input.reps
                            };
                        },
                        weight(data) {
                            return {
                                ...data,
                                done: options.variables?.input.weight
                            };

                        },
                        rest(data) {
                            return {
                                ...data,
                                done: options.variables?.input.rest
                            };
                        }
                    }
                })
            }
        })
    }

    const onSubmitDropsets = async (e: any) => {

        dropsetsForm.getValues().dropsets?.forEach((ds, dsidx) => {
            try {
                endActiveWorkoutDropset({
                    variables: {
                        input: {
                            exerciseId: workoutExerciseId,
                            setId: set.id,
                            dropsetId: ds.id,
                            reps: ds.reps,
                            weight: ds.weight
                        }
                    },
                    update(cache, result, options) {
                        cache.modify({
                            id: cache.identify({ id: options.variables?.input.dropsetId, __typename: 'ActiveStrengthExerciseDropSet' }),
                            fields: {
                                end_at() {
                                    return new Date().toISOString()
                                },
                                reps(data) {
                                    return {
                                        ...data,
                                        done: options.variables?.input.reps
                                    };
                                },
                                weight(data) {
                                    return {
                                        ...data,
                                        done: options.variables?.input.weight
                                    };
                                }
                            }
                        })
                    }
                })
            }
            catch (e) {

            }
        })

    }

    const [endActiveWorkoutSet, { loading: isEndActiveWorkoutSetLoading }] = useEndActiveWorkoutSetMutation();

    const [endActiveWorkoutDropset] = useEndActiveWorkoutDropsetMutation();

    return (
        <div key={idx} className="gap-x-3">
            <div className="flex gap-4 items-center">
                <h3 className="text-lg font-bold">{t("SET")} {idx + 1}</h3>
                {set.rest && <div className='gap-2 items-center flex'>
                    <p className='light'>
                        {t("Rest Time")}
                    </p>
                    <span>
                        {rangeUnitToString(set.rest, t)}
                    </span>
                </div>}
            </div>
            <div className="space-y-2  text-center">
                {set.reps && <div className='gap-2 items-center flex'>
                    <div className="basis-1/2 flex flex-col">
                        <p className='light'>
                            {t("reps")}
                        </p>
                        <div>
                            {rangeUnitToString(set.reps, t)}
                        </div>
                    </div>
                    <span>עשיתי:</span>
                    <TextInput inputProps={{ ...register("reps") }} type="number" maxLength={3} value={String(set?.reps?.done || set?.reps?.val)} />
                </div>}
                {set.weight && <div className='gap-2 items-center flex'>
                    <div className="flex flex-col basis-1/2">
                        <p className='light'>
                            {t("weight")}
                        </p>
                        <span>
                            {rangeUnitToString(set.weight, t)}
                        </span>
                    </div>
                    {!['body', 'max'].includes(set.weight?.unit || "") &&
                        <>
                            <span>עשיתי:</span>
                            <TextInput inputProps={{ ...register("weight") }} type="number" maxLength={3} value={String(set?.weight?.done || set?.weight?.val)} />
                        </>
                    }
                </div>}
                {set.dropsets?.map((ds, dsidx) => {
                    if (!ds) return null;

                    return <ActiveDropsetExercise key={ds.id} ds={ds!} dsidx={dsidx} workoutExerciseId={workoutExerciseId}
                        registerReps={{ ...dropsetsForm.register(`dropsets.${dsidx}.reps`) }}
                        registerWeight={{ ...dropsetsForm.register(`dropsets.${dsidx}.weight`) }}
                    />

                })

                }
            </div>
            <Button loading={isEndActiveWorkoutSetLoading || dropsetsForm.formState.isSubmitting} className="mt-2" link onClick={async () => {
                await handleSubmit(onSubmit)();
                await dropsetsForm.handleSubmit(onSubmitDropsets)();
            }}>
                <CheckIcon className={Boolean(set.end_at) ? 'text-green-500' : 'text-red-500'} fontSize={50} />
            </Button>
        </div>

    )
}

export default ActiveStrengthExercise